"use client";

import React, { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, Edit2, Check, X } from "lucide-react";
import { validateKeyValuePair } from "@/lib/utils/json-field-utils";
import type { JsonValue } from "@/components/shared/types";

interface KeyValuePairRowProps {
  keyName: string;
  value: JsonValue;
  onUpdate: (newKey: string, newValue: JsonValue) => void;
  onDelete: () => void;
  existingKeys?: string[];
}

/**
 * Individual row for each key-value pair
 * Supports inline editing with validation
 */
export function KeyValuePairRow({
  keyName,
  value,
  onUpdate,
  onDelete,
  existingKeys = [],
}: KeyValuePairRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editKey, setEditKey] = useState(keyName);
  const [editValue, setEditValue] = useState(
    typeof value === "string" ? value : JSON.stringify(value)
  );
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(() => {
    setError(null);

    // Validate key
    if (!editKey.trim()) {
      setError("Key cannot be empty");
      return;
    }

    // Check for duplicate keys (excluding current key)
    if (editKey !== keyName && existingKeys.includes(editKey)) {
      setError("Key already exists");
      return;
    }

    // Try to parse value
    let parsedValue: JsonValue;
    try {
      parsedValue = JSON.parse(editValue) as JsonValue;
    } catch {
      // If it's not valid JSON, treat it as a string
      parsedValue = editValue;
    }

    // Validate the pair
    const validation = validateKeyValuePair(editKey, parsedValue);
    if (!validation.valid) {
      setError(validation.error || "Invalid key-value pair");
      return;
    }

    onUpdate(editKey, parsedValue);
    setIsEditing(false);
  }, [editKey, editValue, keyName, existingKeys, onUpdate]);

  const handleCancel = useCallback(() => {
    setEditKey(keyName);
    setEditValue(typeof value === "string" ? value : JSON.stringify(value));
    setError(null);
    setIsEditing(false);
  }, [keyName, value]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  if (isEditing) {
    return (
      <div className="space-y-2 p-3 bg-muted rounded-md border border-input">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 block">Key</label>
            <Input
              value={editKey}
              onChange={(e) => setEditKey(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Enter key"
              className="text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 block">Value</label>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Enter value (JSON or string)"
              className="text-sm"
            />
          </div>
        </div>

        {error && <p className="text-xs text-destructive font-medium">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={handleCancel} className="h-8" title="Close">
            <X className="h-3 w-3" />
          </Button>
          <Button size="sm" onClick={handleSave} className="h-8" title="Confirm">
            <Check className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-2 bg-card border border-input rounded-md hover:bg-muted/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{keyName}:</span>
          <span className="text-sm text-muted-foreground truncate">
            {typeof value === "string" ? value : JSON.stringify(value)}
          </span>
        </div>
      </div>

      <div className="flex gap-1 ml-2 flex-shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsEditing(true)}
          className="h-7 w-7 p-0"
          title="Edit"
        >
          <Edit2 className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
